/**
 * Custom session analytics — funnel + behavior events.
 *
 * Sits next to Microsoft Clarity (visual replay) and Google Analytics (broad
 * traffic) to give us a quantitative funnel: how many sessions reach step 2 of
 * booking, where they drop off, what they rage-click on, what JS errors they
 * hit. Events are batched client-side and POSTed to /api/session-events, which
 * persists them in `session_events` and writes one row per event to Cloudflare
 * Analytics Engine (free, queryable).
 *
 * Usage:
 *   import { trackEvent, trackBookingStep, trackError } from './sessionAnalytics';
 *   trackEvent('book_now_clicked', { tour_id: 4 });
 *   trackBookingStep(2, { tour_id: 4, has_addon: true });
 *   trackError('payment_failed', { reason: 'card_declined' });
 *
 * The Clarity SDK (if loaded) gets a parallel `clarity('event', name)` call so
 * filters/segments line up between the two systems.
 */

const ENDPOINT = '/api/session-events';
const SESSION_KEY = 'nyc_session_id';
const QUEUE_KEY = 'nyc_event_queue';
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE = 50;

function uuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'sess_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 10);
}

function getSessionId() {
  if (typeof window === 'undefined') return null;
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = uuid();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

function getQueue() {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(sessionStorage.getItem(QUEUE_KEY) || '[]'); } catch (_) { return []; }
}

function setQueue(arr) {
  try { sessionStorage.setItem(QUEUE_KEY, JSON.stringify(arr.slice(-MAX_QUEUE))); } catch (_) {}
}

let flushTimer = null;

async function flush() {
  if (typeof window === 'undefined' || !navigator.onLine) return;
  const queue = getQueue();
  if (queue.length === 0) return;
  setQueue([]);
  try {
    const body = JSON.stringify({ events: queue });
    // sendBeacon survives page unload — preferred when the user leaves mid-flush.
    const blob = new Blob([body], { type: 'application/json' });
    if (navigator.sendBeacon && navigator.sendBeacon(ENDPOINT, blob)) return;
    await fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true
    });
  } catch (_) {
    // Re-queue on failure so we don't lose data — capped at MAX_QUEUE total.
    setQueue([...queue, ...getQueue()].slice(-MAX_QUEUE));
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

function recordEvent(name, props = {}) {
  if (typeof window === 'undefined') return;
  const evt = {
    event: name,
    props,
    session_id: getSessionId(),
    url: window.location.pathname,
    referrer: document.referrer || null,
    viewport: window.innerWidth < 768 ? 'mobile' : window.innerWidth < 1024 ? 'tablet' : 'desktop',
    ts: Date.now()
  };
  setQueue([...getQueue(), evt]);
  scheduleFlush();

  // Mirror into Clarity so dashboards line up. Safe no-op if Clarity not loaded.
  if (window.clarity) {
    try { window.clarity('event', name); } catch (_) {}
  }
  // Mirror into gtag for cross-tool reporting.
  if (window.gtag) {
    try { window.gtag('event', name, props); } catch (_) {}
  }
}

// ───── public API ─────

export function trackEvent(name, props) { recordEvent(name, props); }

export function trackPageView(pathname) {
  recordEvent('page_view', { path: pathname || window.location.pathname });
}

export function trackBookingStep(step, props = {}) {
  recordEvent('booking_step', { step, ...props });
}

export function trackBookingAbandoned(props = {}) {
  recordEvent('booking_abandoned', props);
}

export function trackBookingCompleted(props = {}) {
  recordEvent('booking_completed', props);
}

export function trackError(name, props = {}) {
  recordEvent('error', { name, ...props });
}

export function trackRageClick(target) {
  recordEvent('rage_click', { target });
}

export function trackDeadClick(target) {
  recordEvent('dead_click', { target });
}

// ───── auto-instrumentation ─────

/**
 * Hook global click / error / unload listeners. Call once from App.js.
 */
export function installAutoInstrumentation() {
  if (typeof window === 'undefined' || window.__sessionAutoInstalled) return;
  window.__sessionAutoInstalled = true;

  // Rage click detection — 3+ clicks within 500ms on the same target.
  let clickHistory = [];
  document.addEventListener('click', (e) => {
    const now = Date.now();
    const target = describe(e.target);
    clickHistory = clickHistory.filter(c => now - c.t < 500);
    clickHistory.push({ t: now, target });
    const same = clickHistory.filter(c => c.target === target);
    if (same.length >= 3) {
      trackRageClick(target);
      clickHistory = [];
    }
  }, true);

  // Dead click detection — click on element that has no handler / cursor:pointer
  // and produces no visible mutation within 200ms.
  document.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof Element)) return;
    const cs = window.getComputedStyle(t);
    const looksClickable = cs.cursor === 'pointer'
      || t.tagName === 'BUTTON'
      || t.tagName === 'A'
      || t.getAttribute('role') === 'button';
    if (!looksClickable) return;
    let mutated = false;
    const obs = new MutationObserver(() => { mutated = true; });
    obs.observe(document.body, { childList: true, subtree: true, attributes: true, characterData: true });
    setTimeout(() => {
      obs.disconnect();
      if (!mutated && t.tagName !== 'A') trackDeadClick(describe(t));
    }, 200);
  }, true);

  // Global JS errors.
  window.addEventListener('error', (e) => {
    trackError('js_error', {
      message: (e.message || '').slice(0, 250),
      source: (e.filename || '').slice(0, 200),
      line: e.lineno,
      col: e.colno
    });
  });
  window.addEventListener('unhandledrejection', (e) => {
    const reason = e.reason && (e.reason.message || String(e.reason));
    trackError('unhandled_rejection', { reason: (reason || '').slice(0, 250) });
  });

  // Flush remaining events when the user leaves.
  window.addEventListener('pagehide', flush);
  window.addEventListener('beforeunload', flush);
}

function describe(el) {
  if (!(el instanceof Element)) return 'unknown';
  const tag = el.tagName.toLowerCase();
  const id = el.id ? `#${el.id}` : '';
  const cls = el.className && typeof el.className === 'string'
    ? '.' + el.className.split(/\s+/).filter(Boolean).slice(0, 2).join('.')
    : '';
  const text = (el.textContent || '').trim().slice(0, 30);
  return `${tag}${id}${cls}${text ? `[${text}]` : ''}`.slice(0, 120);
}
